import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Plus, GripVertical, FileText, ExternalLink } from 'lucide-react';
import { SortableList } from './SortableList';
import { Dropzone } from './Dropzone';
import { ItemActions } from './ItemActions';
import { RichTextEditor } from './RichTextEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (!u ? '' : u.startsWith('http') ? u : `${BACKEND_URL}${u}`);

/**
 * Generic CMS manager for the article-style collections
 * (hq-events, branch-activities, media-coverage, media-statements).
 *
 * Props:
 *  - slug: URL slug used in /api/admin/articles/{slug}
 *  - collectionName: underlying mongo collection name (e.g. "hq_events"),
 *    used for the publish-toggle endpoint.
 *  - title: human-readable section title (e.g. "HQ Events")
 *  - showSource: whether to show a "Source" input (e.g. for Media Coverage)
 */
const GenericArticleManager = ({ slug, collectionName, title, showSource = false, pdfMode = false }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchItems = async () => {
    const res = await adminApi.get(`/articles/${slug}`);
    setItems(res.data || []);
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const blank = () => ({
    title: '',
    subtitle: '',
    date: '',
    location: '',
    source: '',
    image: '',
    description: '',
    link: '',
    link_label: '',
    order: items.length,
    published: true,
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing.id && items.find((x) => x.id === editing.id)) {
        await adminApi.put(`/articles/${slug}/${editing.id}`, editing);
      } else {
        await adminApi.post(`/articles/${slug}`, { ...editing, id: undefined });
      }
      toast({ title: 'Saved' });
      setEditing(null);
      fetchItems();
    } catch (err) {
      toast({ title: 'Error', description: 'Save failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(`Delete this ${title.toLowerCase()} entry?`)) return;
    await adminApi.delete(`/articles/${slug}/${id}`);
    toast({ title: 'Deleted' });
    fetchItems();
  };

  const handleReorder = async (reordered) => {
    setItems(reordered);
    try {
      await adminApi.put(`/articles/${slug}/reorder`, { ids: reordered.map((r) => r.id) });
    } catch {
      /* ignore */
    }
  };

  const tid = (name) => `${slug}-${name}`;

  return (
    <div className="space-y-6" data-testid={`${slug}-manager`}>
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">{title}</h2>
        <Button
          onClick={() => setEditing(blank())}
          className="bg-[#1565C0]"
          data-testid={tid('add-btn')}
        >
          <Plus className="w-4 h-4 mr-2" /> Add Entry
        </Button>
      </div>

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>{editing.id ? `Edit ${title} entry` : `New ${title} entry`}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  required
                  data-testid={tid('title-input')}
                />
              </div>
              <div>
                <Label>Subtitle / Tagline (optional)</Label>
                <Input
                  value={editing.subtitle}
                  onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>Date (free text)</Label>
                  <Input
                    value={editing.date}
                    onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                    placeholder="12 Aug 2026"
                  />
                </div>
                <div>
                  <Label>Location (optional)</Label>
                  <Input
                    value={editing.location}
                    onChange={(e) => setEditing({ ...editing, location: e.target.value })}
                    placeholder="EWRF HQ, Kuala Lumpur"
                  />
                </div>
              </div>
              {showSource && (
                <div>
                  <Label>Source / Publication</Label>
                  <Input
                    value={editing.source}
                    onChange={(e) => setEditing({ ...editing, source: e.target.value })}
                    placeholder="The Star · Berita Harian · The Edge"
                  />
                </div>
              )}
              {pdfMode ? (
                <div>
                  <Label>Newsletter PDF</Label>
                  {editing.pdf_url && (
                    <div className="mb-2 flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-2 text-sm">
                      <FileText className="w-5 h-5 text-rose-600 shrink-0" />
                      <a
                        href={resolveUrl(editing.pdf_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-1 truncate text-[#1565C0] hover:underline"
                        data-testid={tid('current-pdf-link')}
                      >
                        {editing.pdf_url.split('/').pop()}
                      </a>
                      <a
                        href={resolveUrl(editing.pdf_url)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-gray-500 hover:text-gray-700 shrink-0"
                        title="Open in new tab"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button
                        type="button"
                        onClick={() => setEditing({ ...editing, pdf_url: '' })}
                        className="text-red-600 hover:text-red-700 text-xs font-bold px-2"
                        data-testid={tid('remove-pdf-btn')}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  <Dropzone
                    compact
                    accept="application/pdf"
                    onUpload={(url) => setEditing({ ...editing, pdf_url: url, image: '' })}
                    label="Drop PDF file"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">Upload the monthly newsletter as a PDF. Visitors will see a &quot;Open PDF&quot; button on the public page.</p>
                </div>
              ) : (
                <div>
                  <Label>Image (optional)</Label>
                  {editing.image && (
                    <img src={editing.image} className="w-40 h-28 object-cover rounded mb-2" alt="" />
                  )}
                  <Dropzone
                    compact
                    onUpload={(url) => setEditing({ ...editing, image: url })}
                    label="Drop image"
                  />
                </div>
              )}
              <div>
                <Label>Description</Label>
                <RichTextEditor
                  value={editing.description}
                  onChange={(html) => setEditing({ ...editing, description: html })}
                  placeholder="Write the article body…"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label>External Link (optional)</Label>
                  <Input
                    value={editing.link}
                    onChange={(e) => setEditing({ ...editing, link: e.target.value })}
                    placeholder="https://thestar.com.my/…"
                  />
                </div>
                <div>
                  <Label>Link Label (optional)</Label>
                  <Input
                    value={editing.link_label}
                    onChange={(e) => setEditing({ ...editing, link_label: e.target.value })}
                    placeholder="Read full article"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={editing.published !== false}
                  onCheckedChange={(v) => setEditing({ ...editing, published: v })}
                />
                <Label>Published</Label>
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#1565C0]"
                  data-testid={tid('save-btn')}
                >
                  {loading ? 'Saving…' : 'Save'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <SortableList
        items={items}
        onReorder={handleReorder}
        renderItem={(it) => (
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
              {pdfMode ? (
                <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-600 rounded text-white flex items-center justify-center flex-shrink-0">
                  <FileText className="w-7 h-7" />
                </div>
              ) : it.image ? (
                <img src={it.image} alt="" className="w-16 h-16 object-cover rounded flex-shrink-0" />
              ) : (
                <div className="w-16 h-16 bg-gradient-to-br from-[#1976D2] to-[#1565C0] rounded text-white flex items-center justify-center font-bold flex-shrink-0">
                  {(it.title || '?').slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                {it.source && <p className="text-xs uppercase text-gray-500">{it.source}</p>}
                <h3 className="font-semibold truncate">{it.title}</h3>
                <p className="text-sm text-gray-600 truncate">
                  {it.date}
                  {it.date && it.location && ' · '}
                  {it.location}
                </p>
              </div>
              <ItemActions
                collection={collectionName}
                item={it}
                onChanged={fetchItems}
                onEdit={setEditing}
                onDelete={handleDelete}
              />
            </CardContent>
          </Card>
        )}
      />
      {items.length === 0 && !editing && (
        <p className="text-center text-gray-500 py-8">
          No {title.toLowerCase()} entries yet. Click &quot;Add Entry&quot; to create one.
        </p>
      )}
    </div>
  );
};

export default GenericArticleManager;
